"use client";

import * as S from "./styles.ts";
import { Button, Checkbox, H3, Input, P, Select } from "@template/components";
import { Fragment, useEffect, useRef, useState } from "react";
import Image, { ImageProps } from "next/image";
import { IInputProps } from "../Input";
import { ICheckboxProps } from "../Checkbox";
import { IButtonProps } from "../Button";
import { useGetCountryCode } from "@/lib/helpers/useGetCountryCode.ts";

export interface IFormInput extends IInputProps {
  span?: number;
  name: string;
}

export interface IFormSubmit extends IButtonProps {
  text: string;
  onSubmit?: (data: Record<string, any>, e?: any) => void;
  loading?: boolean;
}

export interface IFormProps {
  inputs: (IFormInput | { divider: true; text?: string })[];
  submit?: IFormSubmit;
  heading?: {
    title?: string;
    subTitle?: string;
    image?: ImageProps;
  };
  value?: Record<string, any>;
  onChange?: (data: Record<string, any>, e?: any) => void;
  checkbox?: ICheckboxProps;
  preSubmit?: any;
  postSubmit?: any;
  simple?: boolean;
  error?: string;
}

const checkValid = (
  value: string | number,
  validation?: IInputProps["validation"]
) => {
  const reg = validation?.regex;

  if (!reg || !value) return true;

  return reg.test(value.toString());
};

const Form = ({
  inputs,
  submit,
  heading,
  value,
  onChange,
  checkbox,
  preSubmit,
  postSubmit,
  simple,
  error,
}: IFormProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { countryCode, setCountryCode, availableCountries } =
    useGetCountryCode();

  const formInputs: IFormInput[] = inputs.filter(
    (input: any) => !("divider" in input && input.divider)
  ) as IFormInput[];

  const [defaultValue, setDefaultValue] = useState(false);

  const usedValue: any = value || defaultValue;

  // check if all required inputs are filled, with no errors
  const checkValidity = () => {
    return formInputs.every((input) => {
      const inputVal = usedValue[input.name];

      if (input.required && !inputVal) return false;

      if (input.error) return false;

      if (checkbox && !checkbox.checked) return false;

      return !(input.validation && !checkValid(inputVal, input.validation));
    });
  };

  const handleChange = () => {
    const usedFunc = onChange || setDefaultValue;

    const formData = new FormData(formRef.current as any);
    const data: Record<string, any> = Object.fromEntries(formData.entries());

    usedFunc(data as any);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!checkValidity()) return;

    const formData = new FormData(formRef.current as any);
    const data = Object.fromEntries(formData.entries());

    if (submit?.onSubmit) {
      submit.onSubmit(data, e);
    }
  };

  useEffect(() => {
    if (onChange && value) {
      onChange({
        ...value,
        countryCode: countryCode, // Sending this "hidden" key to parent
      });
    }
  }, [countryCode]);

  // @ts-ignore
  return (
    <S.FormWrapper $simple={simple}>
      <S.FormContainer>
        {(heading?.title || heading?.subTitle || heading?.image) && (
          <S.FormHeadingContainer>
            {heading?.image && (
              <Image
                src={heading.image.src}
                alt={heading.image.alt}
                height={40}
                width={100}
              />
            )}
            <S.FormHeadingTextContainer>
              {heading?.title && <H3>{heading.title}</H3>}
              {heading?.subTitle && <P>{heading.subTitle}</P>}
            </S.FormHeadingTextContainer>
          </S.FormHeadingContainer>
        )}
        <S.Form ref={formRef}>
          <S.FormInputsContainer>
            {inputs.map((input, index) =>
              "divider" in input && input.divider ? (
                <S.Divider key={index}>
                  <span />
                  <p>{input.text}</p>
                  <span />
                </S.Divider>
              ) : (
                !("divider" in input) && (
                  <S.FormInputWrapper
                    key={index}
                    style={{
                      gridColumn: `span ${typeof input.span === "number" ? input.span : 12}`,
                    }}
                  >
                    {input.type === "select" ? (
                      <Select
                        {...input}
                        selected={input.selected || usedValue[input.name]}
                        onChange={(val: any) =>
                          onChange &&
                          onChange({
                            // @ts-ignore
                            ...usedValue,
                            [input.name]: val,
                          })
                        }
                        fullWidth
                        // @ts-ignore
                        span={undefined}
                        ariaLabel={`Dropdown for ${input.label} field`}
                      />
                    ) : input.type === "tel" ? (
                      <Fragment>
                        <S.InputLabel>
                          {input.label}
                          {input.required && !input.hiddenRequired && (
                            <S.RequiredStar>*</S.RequiredStar>
                          )}
                        </S.InputLabel>
                        <S.SearchInput>
                          <Select
                            options={availableCountries}
                            variant="solid"
                            selected={countryCode}
                            onChange={setCountryCode}
                            search
                            fullWidth
                          />
                          <Input
                            name={input.name}
                            fullWidth
                            value={usedValue[input.name]}
                            placeholder={input.placeholder}
                            ariaLabel={`Input for ${input.label} field`}
                            onChange={(val: any) =>
                              onChange &&
                              onChange({
                                // @ts-ignore
                                ...usedValue,
                                [input.name]: val,
                              })
                            }
                          />
                        </S.SearchInput>
                      </Fragment>
                    ) : (
                      <Input
                        {...input}
                        value={usedValue[input.name]}
                        onChange={(val: any) =>
                          onChange &&
                          onChange({
                            // @ts-ignore
                            ...usedValue,
                            [input.name]: val,
                          })
                        }
                        fullWidth
                        // @ts-ignore
                        span={undefined}
                        ariaLabel={`Input for ${input.label} field`}
                      />
                    )}
                  </S.FormInputWrapper>
                )
              )
            )}
            {checkbox && <Checkbox {...checkbox} />}
          </S.FormInputsContainer>
          {preSubmit && <S.LightChild>{preSubmit}</S.LightChild>}
          {error && <S.Error>{error}</S.Error>}
          {submit && (
            <Button
              size="lg"
              type="submit"
              disabled={!checkValidity()}
              loading={submit?.loading}
              fullWidth
              onClick={handleSubmit}
              {...submit}
              // @ts-ignore
              text={undefined}
            >
              {submit.text}
            </Button>
          )}
          {postSubmit && postSubmit}
        </S.Form>
      </S.FormContainer>
    </S.FormWrapper>
  );
};

export default Form;
